import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import { authService } from '../../services/supabaseService';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import InterventionGrid from './components/interventionGrid';
import QuoteModal from './components/QuoteModal'; // Import du modal de devis
import { APP_NAME } from '../../constants';

const Interventionmanagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  
  // États pour le modal de devis
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedInterventionForQuote, setSelectedInterventionForQuote] = useState(null);
  const [quoteCreationStatus, setQuoteCreationStatus] = useState(null); // 'success', 'error', null
  const [quoteListVersion, setQuoteListVersion] = useState(0);
  
  // États pour l'utilisateur connecté
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupérer l'utilisateur connecté
  useEffect(() => {  
    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        const { user, error: userError } = await authService.getCurrentUser();
        
        if (userError) {
          console.error('Error fetching current user:', userError);
          setError('Failed to load user data');
          return;
        }

        if (user) {
          setCurrentUser({
            id: user.id,
            name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Utilisateur',
            email: user.email,
            role: user.user_metadata?.role || 'user',
          });
        } else {
          setError('No user logged in');
        }
      } catch (err) {
        console.error('Error in fetchCurrentUser:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    setShowBulkOperations(selectedItems.length > 0);
  }, [selectedItems]);

  // Gestionnaire pour ouvrir le modal de devis
  const handleOpenQuoteModal = (intervention) => {
    setSelectedInterventionForQuote(intervention);
    setIsQuoteModalOpen(true);
    setQuoteCreationStatus(null);
  };

  // Gestionnaire pour la création réussie d'un devis
  const handleQuoteCreated = (quoteData) => {
    console.log('Devis créé avec succès:', quoteData);
    setQuoteCreationStatus('success');
    setQuoteListVersion((v) => v + 1);
    alert(`Devis ${quoteData.reference} créé avec succès !`);
  };

  // Gestionnaire pour la fermeture du modal
  const handleCloseQuoteModal = () => {
    setIsQuoteModalOpen(false);
    setSelectedInterventionForQuote(null);
    // On ne réinitialise pas le statut immédiatement pour permettre les animations
    setTimeout(() => setQuoteCreationStatus(null), 300);
  };

  const handleSelectionChange = (items) => {
    setSelectedItems(items);
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const handleBulkAction = (actionId, items, data) => {
    console.log('Bulk action:', actionId, items, data);
    
    try {
      switch (actionId) {
        case 'update-status':
          console.log(`Updating status to "${data?.value}" for ${items.length} interventions`);
          break;
        case 'assign-technician':
          console.log(`Assigning technician "${data?.value}" to ${items.length} interventions`);
          break;
        case 'export-data':
          console.log(`Exporting data for ${items.length} interventions`);
          break;
        default:
          console.log(`Unknown action: ${actionId}`);
      }
    } catch (err) {
      console.error('Error in bulk action:', err);
      setError(err.message);
    }
    
    setSelectedItems([]);
  };

  const handleQuickAction = (actionId, items) => {
    try {
      switch (actionId) {
        case 'export-data':
          console.log('Exporting intervention data');
          break;
        case 'clear-selection':
          handleClearSelection();
          break;
        default:
          handleBulkAction(actionId, items, {});
      }
    } catch (err) {
      console.error('Error in quick action:', err);
      setError(err.message);
    }
  };

  // Loading state principal
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement des données...</p>
        </div>
      </div>
    );
  }

  // Error state principal
  if (error && !currentUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Erreur de chargement</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Réessayer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole={currentUser?.role}
        userName={`${currentUser?.name} - ${APP_NAME}`}
      />
      
      <main className={`pt-16 transition-layout`}>
        <div className="p-6">
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Gestion des interventions</h1>
              <p className="text-muted-foreground mt-1">
                Suivre et gérer toutes les interventions planifiées et en cours
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Vous pouvez ajouter d'autres boutons d'action ici */}
              {selectedItems.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearSelection}
                  iconName="X"
                  iconSize={16}
                >
                  Effacer la sélection ({selectedItems.length})
                </Button>
              )}
            </div>
          </div>

          {/* Notification de succès pour la création de devis */}
          {quoteCreationStatus === 'success' && (
            <div className="mb-4 p-4 bg-green-100 border border-green-300 rounded-lg text-green-700 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Devis créé avec succès !</span>
              </div>
              <button 
                onClick={() => setQuoteCreationStatus(null)} 
                className="text-green-800 hover:text-green-900"
              >
                ×
              </button>
            </div>
          )}

          {/* Error display pour les opérations */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg text-red-700 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
              <button 
                onClick={() => setError(null)} 
                className="text-red-800 hover:text-red-900"
              >
                ×
              </button>
            </div>
          )}

          {/* Main Content */}
          {!loading && (
            <div>
              <InterventionGrid
                onSelectionChange={handleSelectionChange}
                selectedItems={selectedItems}
                currentUserRole={currentUser?.role}
                currentUserId={currentUser?.id}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onOpenQuoteModal={handleOpenQuoteModal}
                quoteListVersion={quoteListVersion}
              />
            </div>
          )}
        </div>
      </main>

      {/* Modal de devis */}
      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={handleCloseQuoteModal}
        intervention={selectedInterventionForQuote}
        onQuoteCreated={handleQuoteCreated}
      />
    </div>
  );
};

export default Interventionmanagement;